import PropTypes from 'prop-types';
import FormRow from '@/components/core/form/FormRow';
import { useFormContext } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { isDefined } from '@/utils/miscUtils';
import { useDerivedParam } from '@/stores/derivedCacheStore';
import { useDerivedValueFetch } from '@/hooks/useDerivedValueFetch';
import ConversionField from '../../ConversionField';
import InputBase from '@/components/core/form/InputBase';
import usePatchParams from '../../usePatchParams';
import { NumericFormatInput } from '@/components/core/form/NumericFormatInput';
import ErrorMessage from '../ErrorMessage';
import { useParamsContext } from '@/components/providers/useParamsContext';
import { DISPLAY_TYPES } from '@/consts';

const DerivedField = ({
  param,
  paramSchema,
  name,
  parentName,
  onChange,
  displayType = DISPLAY_TYPES.row,
  isGroupField = false,
}) => {
  const {
    label,
    type,
    tooltip,
    source,
    convertible_units: convertibleUnits,
    editable,
    display_options: displayOptions,
  } = paramSchema;

  const { primary: { value, is_default: isDefault, default: defaultValue } = {}, unit } = param ?? {};
  const { fetch, changed_dependencies: changedDependencies } = value ?? {};
  const {
    register,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = isGroupField
    ? errors?.[parentName]?.message?.[paramSchema.name]?.primary?.value
    : errors?.[name];

  const { readOnly } = useParamsContext();
  const disabled = !editable || readOnly;

  const conversionName = parentName ? `${parentName}.conversion.${name}` : `conversion.${name}`;
  const { patchPrimary } = usePatchParams();
  const results = useDerivedParam(name);
  const numericValueRef = useRef(null);

  const shouldFetch = fetch || !results;
  const currentValue = results?.scalar ?? results ?? '';

  const { isLoading } = useDerivedValueFetch({
    name,
    shouldFetch,
    changedDependencies,
  });

  const registration = register(name, {
    valueAsNumber: type === 'numeric',
    disabled,
  });

  const handleChange = event => {
    event.preventDefault();
  };

  const events = {
    onChange: handleChange,
    onBlur: async e => {
      if (disabled) return;

      const prevValue = getValues(name);
      const value = type === 'numeric' ? (numericValueRef.current ?? parseFloat(e.target.value)) : e.target.value;

      if (prevValue !== value) {
        if (isGroupField && onChange) {
          // Group field behavior
          setValue(name, value, { shouldValidate: true, shouldDirty: true });
          registration.onChange(e);
          onChange(e);
        } else {
          // Regular field behavior
          await patchPrimary(name, value);
        }
      } else {
        setValue(name, value, { shouldValidate: true, shouldDirty: false });
      }
    },
  };

  useEffect(() => {
    const value = results?.scalar ?? results;

    if (isDefined(value)) {
      setValue(name, value, { shouldValidate: true, shouldDirty: false });
    }
  }, [results, name, setValue]);

  if (!isDefined(results) && !isLoading) {
    return null;
  }

  const inputProps = {
    id: name,
    name,
    defaultValue: currentValue,
    onBlur: events.onBlur,
    onChange: events.onChange,
    disabled,
    readOnly: isGroupField ? readOnly : disabled,
    placeholder: defaultValue?.toString(),
    hasError: !!errorMessage,
    isDefault: isDefault,
  };

  const conversionField = convertibleUnits?.length > 0 && (
    <ConversionField
      className={displayType === DISPLAY_TYPES.table ? 'w-full' : ''}
      unit={isGroupField ? unit : { value: results?.unit }}
      name={conversionName}
      paramName={name}
      units={convertibleUnits}
      disabled={disabled}
      readOnly={isGroupField ? readOnly : disabled}
    />
  );

  const isReadOnly = displayOptions?.includes('read_only');

  if (displayType === DISPLAY_TYPES.table) {
    return (
      <>
        <tr>
          <td className="text-sm p-2 pl-4">{label}</td>
          <td className="p-2">
            {isReadOnly ? (
              <div className="flex items-center text-sm">{currentValue}</div>
            ) : type === 'numeric' ? (
              <NumericFormatInput
                control={control}
                {...inputProps}
                onNumericChange={value => {
                  numericValueRef.current = value;
                }}
              />
            ) : (
              <InputBase {...registration} {...inputProps} />
            )}
          </td>
          <td className="p-2 pr-4 w-full">{!isReadOnly && conversionField}</td>
        </tr>
        {errorMessage && (
          <tr>
            <td></td>
            <td className="p-0 pl-1">
              <ErrorMessage text={errorMessage} className="p-0" />
            </td>
            <td></td>
          </tr>
        )}
      </>
    );
  }

  return (
    <FormRow label={label} tooltip={tooltip} source={source} name={name}>
      <div className="flex flex-col flex-1 min-w-0">
        <div className={`${convertibleUnits?.length > 0 && !isReadOnly ? 'join flex' : ''}`}>
          {isReadOnly ? (
            <div className="flex items-center h-full text-sm max-w-[30rem] break-words">{currentValue}</div>
          ) : type === 'numeric' ? (
            <NumericFormatInput
              control={control}
              {...inputProps}
              onNumericChange={value => {
                numericValueRef.current = value;
              }}
            />
          ) : (
            <InputBase {...registration} {...inputProps} />
          )}
          {!isReadOnly && conversionField}
        </div>
      </div>
    </FormRow>
  );
};

DerivedField.propTypes = {
  param: PropTypes.object,
  paramSchema: PropTypes.object,
  name: PropTypes.string,
  parentName: PropTypes.string,
  onChange: PropTypes.func,
  displayType: PropTypes.oneOf(['row', 'table']),
  isGroupField: PropTypes.bool,
  children: PropTypes.node,
};

export default DerivedField;
